import React, { useState, useEffect } from 'react';
import Navbar from '../../components/Navbar';
import { db } from '../../firebase';
import { collection, getDocs, addDoc, deleteDoc, doc, updateDoc, query, orderBy } from 'firebase/firestore';
import { FaTrash, FaEdit, FaImage, FaLink, FaPlus, FaTimes, FaEye, FaArrowUp, FaArrowDown } from 'react-icons/fa';
import { motion } from 'framer-motion';

const ProjectsDashboard = () => {
  const [projects, setProjects] = useState([]);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [image, setImage] = useState('');
  const [category, setCategory] = useState('');
  const [link, setLink] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [isFeatured, setIsFeatured] = useState(false);
  const projectsCollection = collection(db, 'projects');

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const q = query(projectsCollection, orderBy('order', 'asc'));
      const projectSnapshot = await getDocs(q);
      const projectList = projectSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      setProjects(projectList);
    } catch (error) {
      console.error('Error fetching projects:', error);
      const projectSnapshot = await getDocs(projectsCollection);
      const projectList = projectSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      setProjects(projectList);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const formData = new FormData();
    formData.append('image', file);
    
    try {
      const response = await fetch(
        'https://api.imgbb.com/1/upload?key=2d072cefa31ad1abcbcd67b25e591c43',
        {
          method: 'POST',
          body: formData,
        }
      );
      const data = await response.json();
      setImage(data.data.url);
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Failed to upload image. Please try again.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const projectData = {
        title,
        description,
        image,
        category,
        link,
        isFeatured: editingId ? projects.find(p => p.id === editingId)?.isFeatured : isFeatured,
        order: editingId ? projects.find(p => p.id === editingId)?.order : projects.length,
        createdAt: editingId ? projects.find(p => p.id === editingId)?.createdAt : new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      if (editingId) {
        await updateDoc(doc(db, 'projects', editingId), projectData);
        setSuccessMessage('Project updated successfully!');
      } else {
        await addDoc(projectsCollection, projectData);
        setSuccessMessage('Project created successfully!');
        resetForm();
      }
      
      setTimeout(() => setSuccessMessage(''), 3000);
      fetchProjects();
    } catch (error) {
      console.error('Error saving project:', error);
      alert('Failed to save project. Please try again.');
    }
    
    setIsSubmitting(false);
    setEditingId(null);
  };

  const resetForm = () => {
    setTitle('');
    setDescription('');
    setImage('');
    setCategory('');
    setLink('');
    setIsFeatured(false);
  };

  const deleteProject = async (id) => {
    if (window.confirm('Are you sure you want to delete this project?')) {
      try {
        await deleteDoc(doc(db, 'projects', id));
        fetchProjects();
      } catch (error) {
        console.error('Error deleting project:', error);
        alert('Failed to delete project. Please try again.');
      }
    }
  };

  const startEdit = (project) => {
    setEditingId(project.id);
    setTitle(project.title);
    setDescription(project.description);
    setImage(project.image || '');
    setCategory(project.category || '');
    setLink(project.link || '');
    setIsFeatured(project.isFeatured || false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const cancelEdit = () => {
    setEditingId(null);
    resetForm();
  };

  const toggleFeatured = async (id, currentFeatured) => {
    try {
      await updateDoc(doc(db, 'projects', id), { isFeatured: !currentFeatured });
      fetchProjects();
    } catch (error) {
      console.error('Error updating featured status:', error);
    }
  };

  const moveProject = async (id, direction) => {
    const currentIndex = projects.findIndex(p => p.id === id);
    if (currentIndex === -1) return;
    
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= projects.length) return;

    const currentProject = projects[currentIndex];
    const targetProject = projects[newIndex];

    try {
      await updateDoc(doc(db, 'projects', id), { order: targetProject.order || newIndex });
      await updateDoc(doc(db, 'projects', targetProject.id), { order: currentProject.order || currentIndex });
      fetchProjects();
    } catch (error) {
      console.error('Error moving project:', error);
    }
  };

  return (
    <div className="min-h-screen bg-perspective-carbon text-perspective-sand relative overflow-hidden">
      <div className="pointer-events-none absolute inset-0 opacity-60">
        <div className="absolute inset-0 bg-grid-soft [background-size:28px_28px] opacity-20" />
      </div>

      <div className="relative z-10">
        <Navbar />
        <div className="h-16" />

        <div className="max-w-7xl mx-auto px-4 py-8">
          <motion.div 
            className="mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="text-4xl font-bold text-white mb-2">Projects Management</h1>
            <p className="text-perspective-cloud/80">Manage your portfolio projects with ease</p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Project Form */}
            <motion.div
              className="bg-white/5 backdrop-blur border border-white/10 rounded-3xl shadow-elevated overflow-hidden"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <div className="border-b border-white/10 p-6">
                <h2 className="text-2xl font-semibold text-white">
                  {editingId ? 'Edit Project' : 'Create New Project'}
                </h2>
              </div>
              <div className="p-6">
                {successMessage && (
                  <motion.div 
                    className="mb-6 bg-perspective-sage/15 border border-perspective-sage/30 text-perspective-sage p-4 rounded-xl"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    {successMessage}
                  </motion.div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label className="block text-sm font-semibold text-perspective-cloud/80 mb-2">Project Title</label>
                    <input
                      type="text"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      required
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-perspective-cloud/50 focus:outline-none focus:border-perspective-sage focus:ring-2 focus:ring-perspective-sage/20 transition-all"
                      placeholder="Enter project title"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-perspective-cloud/80 mb-2">Description</label>
                    <textarea
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      required
                      rows={4}
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-perspective-cloud/50 focus:outline-none focus:border-perspective-sage focus:ring-2 focus:ring-perspective-sage/20 transition-all resize-none"
                      placeholder="Describe the project..."
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-perspective-cloud/80 mb-2">Category</label>
                      <input
                        type="text"
                        value={category}
                        onChange={(e) => setCategory(e.target.value)}
                        required
                        className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-perspective-cloud/50 focus:outline-none focus:border-perspective-sage focus:ring-2 focus:ring-perspective-sage/20 transition-all"
                        placeholder="e.g., Web Development | 2024"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-perspective-cloud/80 mb-2">Project Link</label>
                      <input
                        type="url"
                        value={link}
                        onChange={(e) => setLink(e.target.value)}
                        required
                        className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-perspective-cloud/50 focus:outline-none focus:border-perspective-sage focus:ring-2 focus:ring-perspective-sage/20 transition-all"
                        placeholder="https://..."
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-perspective-cloud/80 mb-2">Project Image</label>
                    <div className="relative">
                      <label className="flex items-center gap-3 px-4 py-3 rounded-xl border border-white/10 cursor-pointer hover:border-perspective-sage/50 transition-colors bg-white/5">
                        <FaImage className="w-5 h-5 text-perspective-sage" />
                        <span className="text-perspective-cloud/80">Choose Image</span>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleImageUpload}
                          required={!editingId || !image}
                          className="hidden"
                        />
                      </label>
                      {image && (
                        <div className="mt-4 relative rounded-xl overflow-hidden border border-white/10">
                          <img
                            src={image}
                            alt="Preview"
                            className="w-full h-48 object-cover"
                          />
                          <button
                            type="button"
                            onClick={() => setImage('')}
                            className="absolute top-2 right-2 p-2 bg-perspective-carbon/90 rounded-full hover:bg-perspective-carbon transition-colors"
                          >
                            <FaTimes className="w-4 h-4 text-white" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={isFeatured}
                        onChange={(e) => setIsFeatured(e.target.checked)}
                        className="w-5 h-5 rounded bg-white/5 border-white/10 text-perspective-sage focus:ring-perspective-sage/20"
                      />
                      <span className="text-perspective-cloud/80">Featured Project</span>
                    </label>
                  </div>

                  <div className="flex gap-4">
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="flex-1 group bg-perspective-sage text-perspective-carbon px-6 py-3 rounded-xl font-semibold hover:scale-105 transition-all duration-300 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {isSubmitting ? 'Saving...' : editingId ? 'Update Project' : 'Create Project'}
                      <FaPlus className="group-hover:rotate-90 transition-transform" />
                    </button>
                    {editingId && (
                      <button
                        type="button"
                        onClick={cancelEdit}
                        className="px-6 py-3 rounded-xl border border-white/10 text-perspective-cloud/80 hover:border-white/20 transition-colors"
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                </form>
              </div>
            </motion.div>

            {/* Projects List */}
            <motion.div
              className="bg-white/5 backdrop-blur border border-white/10 rounded-3xl shadow-elevated overflow-hidden"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <div className="border-b border-white/10 p-6">
                <h2 className="text-2xl font-semibold text-white">Your Projects ({projects.length})</h2>
              </div>
              <div className="p-6">
                {projects.length === 0 ? (
                  <div className="text-center py-12">
                    <FaImage className="w-16 h-16 text-perspective-cloud/30 mx-auto mb-4" />
                    <p className="text-perspective-cloud/60">No projects yet. Create your first project!</p>
                  </div>
                ) : (
                  <div className="space-y-4 max-h-[600px] overflow-y-auto">
                    {projects.map((project, index) => (
                      <motion.div
                        key={project.id}
                        className="bg-white/5 border border-white/10 rounded-2xl p-4 hover:border-perspective-sage/30 transition-all duration-300"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                      >
                        <div className="flex items-start gap-4">
                          {project.image && (
                            <img
                              src={project.image}
                              alt={project.title}
                              className="w-20 h-20 object-cover rounded-xl flex-shrink-0"
                            />
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <h3 className="text-lg font-semibold text-white truncate">{project.title}</h3>
                                  {project.isFeatured && (
                                    <span className="px-2 py-0.5 bg-perspective-sage/20 text-perspective-sage text-xs font-medium rounded-full border border-perspective-sage/30">
                                      Featured
                                    </span>
                                  )}
                                </div>
                                <p className="text-sm text-perspective-cloud/70 mb-2 line-clamp-2">{project.description}</p>
                                <div className="flex items-center gap-3 text-xs text-perspective-cloud/60">
                                  <span className="bg-white/5 px-2 py-1 rounded">{project.category}</span>
                                  {project.link && (
                                    <a href={project.link} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 hover:text-perspective-sage transition-colors">
                                      <FaLink className="w-3 h-3" />
                                      View
                                    </a>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 mt-3">
                              <button
                                onClick={() => startEdit(project)}
                                className="p-2 rounded-lg bg-white/5 border border-white/10 hover:bg-perspective-sage/20 hover:border-perspective-sage/30 transition-all"
                                title="Edit"
                              >
                                <FaEdit className="w-4 h-4 text-perspective-sage" />
                              </button>
                              <button
                                onClick={() => toggleFeatured(project.id, project.isFeatured)}
                                className="p-2 rounded-lg bg-white/5 border border-white/10 hover:bg-perspective-sage/20 hover:border-perspective-sage/30 transition-all"
                                title={project.isFeatured ? 'Unfeature' : 'Feature'}
                              >
                                <FaEye className={`w-4 h-4 ${project.isFeatured ? 'text-perspective-sage' : 'text-perspective-cloud/60'}`} />
                              </button>
                              <div className="flex flex-col gap-1">
                                <button
                                  onClick={() => moveProject(project.id, 'up')}
                                  disabled={index === 0}
                                  className="p-1 rounded bg-white/5 border border-white/10 hover:bg-perspective-sage/20 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                                  title="Move up"
                                >
                                  <FaArrowUp className="w-3 h-3 text-perspective-cloud/60" />
                                </button>
                                <button
                                  onClick={() => moveProject(project.id, 'down')}
                                  disabled={index === projects.length - 1}
                                  className="p-1 rounded bg-white/5 border border-white/10 hover:bg-perspective-sage/20 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                                  title="Move down"
                                >
                                  <FaArrowDown className="w-3 h-3 text-perspective-cloud/60" />
                                </button>
                              </div>
                              <button
                                onClick={() => deleteProject(project.id)}
                                className="p-2 rounded-lg bg-white/5 border border-white/10 hover:bg-red-500/20 hover:border-red-500/30 transition-all ml-auto"
                                title="Delete"
                              >
                                <FaTrash className="w-4 h-4 text-red-400" />
                              </button>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectsDashboard;
